export type ProjectFileEntry = {
  path: string;
  name: string;
  type: "file" | "folder";
  parentPath: string;
  content?: string;
  order?: number;
};

export type ProjectFileTreeNode<T extends ProjectFileEntry = ProjectFileEntry> = T & {
  children: ProjectFileTreeNode<T>[];
};

export const WRITING_STUDIO_ROOT_FOLDERS = [
  "Chapters",
  "Characters",
  "World Building",
  "Notes",
] as const;

export const WRITING_STUDIO_ROOT_FILES = ["outline.md"] as const;

export const WRITING_STUDIO_SIDEBAR_HIDDEN_FOLDERS = [
  "Marketing Studio",
  "Audiobook Studio",
] as const;

export const WRITING_STUDIO_PROTECTED_PATHS = [
  ...WRITING_STUDIO_ROOT_FOLDERS,
  ...WRITING_STUDIO_ROOT_FILES,
  "Notes/story-bible.md",
  "Marketing Studio",
  "Audiobook Studio",
  "Audiobook Studio/Transcripts",
] as const;

const buildWelcomeContent = (projectName: string) => `# Welcome to ${projectName}

This is your writing workspace. Here's how to get started:

## Quick Tips

1. Use the AI Assistant to brainstorm, revise, and keep momentum.
2. Create new chapters inside the Chapters folder to keep downstream tools working.
3. Use canvas files when you want to map scenes, characters, and plot visually.
4. Use focus mode when you want a cleaner drafting view.

## Keyboard Shortcuts

- Cmd/Ctrl + S: Save current file
- Cmd/Ctrl + N: New file
- Cmd/Ctrl + P: Quick file search
- Cmd/Ctrl + .: Toggle focus mode

Happy writing!
`;

export const getDefaultProjectStructure = (projectName = "Share-Pen"): ProjectFileEntry[] => [
  { path: "Chapters", name: "Chapters", type: "folder", parentPath: "", order: 0 },
  { path: "Characters", name: "Characters", type: "folder", parentPath: "", order: 1 },
  { path: "World Building", name: "World Building", type: "folder", parentPath: "", order: 2 },
  { path: "Notes", name: "Notes", type: "folder", parentPath: "", order: 3 },
  { path: "outline.md", name: "outline.md", type: "file", parentPath: "", content: "", order: 4 },
  {
    path: "Notes/getting_started.md",
    name: "getting_started.md",
    type: "file",
    parentPath: "Notes",
    content: buildWelcomeContent(projectName),
    order: 0,
  },
];

export const joinProjectFilePath = (parentPath: string, name: string) =>
  parentPath ? `${parentPath}/${name}` : name;

export const getProjectFileParentPath = (path: string) =>
  path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "";

export const getProjectFileName = (path: string) =>
  path.includes("/") ? path.slice(path.lastIndexOf("/") + 1) : path;

export const isNestedProjectPath = (path: string, parentPath: string) =>
  Boolean(parentPath) && path.startsWith(`${parentPath}/`);

export const shouldHideProjectPathInSidebar = (
  path: string,
  hiddenFolders: readonly string[] = WRITING_STUDIO_SIDEBAR_HIDDEN_FOLDERS,
) => hiddenFolders.some((folder) => path === folder || path.startsWith(`${folder}/`));

export const isProtectedProjectPath = (path: string) =>
  WRITING_STUDIO_PROTECTED_PATHS.includes(
    path as (typeof WRITING_STUDIO_PROTECTED_PATHS)[number],
  );

export const sortProjectEntries = <T extends ProjectFileEntry>(entries: readonly T[]) =>
  [...entries].sort((a, b) => {
    const orderA = typeof a.order === "number" ? a.order : Number.MAX_SAFE_INTEGER;
    const orderB = typeof b.order === "number" ? b.order : Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
  });

export const buildVisibleProjectFileTree = <T extends ProjectFileEntry>(
  files: readonly T[],
  hiddenFolders: readonly string[] = WRITING_STUDIO_SIDEBAR_HIDDEN_FOLDERS,
): ProjectFileTreeNode<T>[] => {
  const visibleFiles = files.filter((file) => !shouldHideProjectPathInSidebar(file.path, hiddenFolders));
  const nodes = new Map<string, ProjectFileTreeNode<T>>();
  const roots: ProjectFileTreeNode<T>[] = [];

  for (const file of visibleFiles) {
    nodes.set(file.path, { ...file, children: [] });
  }

  for (const file of visibleFiles) {
    const node = nodes.get(file.path);
    if (!node) continue;

    const parent = file.parentPath ? nodes.get(file.parentPath) : null;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortTree = (items: ProjectFileTreeNode<T>[]): ProjectFileTreeNode<T>[] =>
    sortProjectEntries(items).map((item) => ({
      ...item,
      children: sortTree(item.children),
    }));

  return sortTree(roots);
};

export const hasExactProjectPathConflict = <T extends Pick<ProjectFileEntry, "path">>(
  files: readonly T[],
  path: string,
  excludePath?: string,
) => files.some((file) => file.path === path && file.path !== excludePath);

export const hasSiblingNameConflict = <
  T extends Pick<ProjectFileEntry, "path" | "parentPath">,
>(
  files: readonly T[],
  parentPath: string,
  name: string,
  excludePath?: string,
) => hasExactProjectPathConflict(files, joinProjectFilePath(parentPath, name), excludePath);
